/**
 * Контракты справочника атрибутов и групп атрибутов (E04.3, ЧТЗ разд. 10.2).
 *
 * Атрибут — переиспользуемая характеристика (производительность, ширина ленты).
 * Как именно он ведёт себя в конкретной модели (порядок, обязательность, формула),
 * задаётся отдельно схемой атрибутов модели — E04.3a.
 */

import { z } from "zod";
import { apiSuccessSchema } from "./envelope";

/** Тип значения атрибута (Архитектура 7.3). */
export const attributeValueTypeSchema = z.enum(["number", "text", "list"]);

export type AttributeValueType = z.infer<typeof attributeValueTypeSchema>;

const nameSchema = z.string().trim().min(1).max(255);
const sortOrderSchema = z.number().int().min(0).max(100_000);

/**
 * Имя для формул: латиница, цифры и подчёркивание. Вычислитель связывает
 * переменные через маппинг, а не по имени (И-4) — это подсказка человеку.
 */
export const formulaNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z][A-Za-z0-9_]*$/, "Имя для формул: латиница, цифры и подчёркивание");

/** Единица измерения из справочника. */
export const unitSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(64),
});

export type Unit = z.infer<typeof unitSchema>;

/** Список значений для атрибутов типа «список» (E04.5, ФТ-1.5.1). */
export const valueListSummarySchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  itemCount: z.number().int().nonnegative(),
});

export type ValueListSummary = z.infer<typeof valueListSummarySchema>;

/** Секция формы: «Характеристики конвейера», «Характеристики материала» и др. */
export const attributeGroupSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  sortOrder: sortOrderSchema,
  /** В скольких моделях используется секция — видно, что затронет удаление. */
  usageCount: z.number().int().nonnegative(),
});

export type AttributeGroup = z.infer<typeof attributeGroupSchema>;

/** Тело `POST /api/v1/admin/attribute-groups`. */
export const createAttributeGroupRequestSchema = z.object({
  name: nameSchema,
  sortOrder: sortOrderSchema.default(0),
});

export type CreateAttributeGroupRequest = z.infer<typeof createAttributeGroupRequestSchema>;

/** Тело `PATCH /api/v1/admin/attribute-groups/{id}`. */
export const updateAttributeGroupRequestSchema = z
  .object({ name: nameSchema.optional(), sortOrder: sortOrderSchema.optional() })
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateAttributeGroupRequest = z.infer<typeof updateAttributeGroupRequestSchema>;

/** Атрибут справочника. */
export const attributeSchema = z.object({
  id: z.string().uuid(),
  name: nameSchema,
  formulaName: formulaNameSchema.nullable(),
  valueType: attributeValueTypeSchema,
  /** Заполняется только для типа «список». */
  valueListId: z.string().uuid().nullable(),
  valueListName: nameSchema.nullable(),
  unitId: z.string().uuid().nullable(),
  unitName: z.string().nullable(),
  /** В скольких моделях атрибут задействован — удаление блокируется. */
  usageCount: z.number().int().nonnegative(),
});

export type Attribute = z.infer<typeof attributeSchema>;

const attributeFieldsSchema = z.object({
  name: nameSchema,
  formulaName: formulaNameSchema.nullable().default(null),
  valueType: attributeValueTypeSchema,
  valueListId: z.string().uuid().nullable().default(null),
  unitId: z.string().uuid().nullable().default(null),
});

/** Тело `POST /api/v1/admin/attributes`. */
export const createAttributeRequestSchema = attributeFieldsSchema;

export type CreateAttributeRequest = z.infer<typeof createAttributeRequestSchema>;

/** Тело `PATCH /api/v1/admin/attributes/{id}`. */
export const updateAttributeRequestSchema = attributeFieldsSchema
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateAttributeRequest = z.infer<typeof updateAttributeRequestSchema>;

/** Конверты ответов. */
export const attributeListResponseSchema = apiSuccessSchema(z.array(attributeSchema));
export const attributeResponseSchema = apiSuccessSchema(attributeSchema);
export const attributeGroupListResponseSchema = apiSuccessSchema(z.array(attributeGroupSchema));
export const attributeGroupResponseSchema = apiSuccessSchema(attributeGroupSchema);
export const unitListResponseSchema = apiSuccessSchema(z.array(unitSchema));
export const valueListSummaryListResponseSchema = apiSuccessSchema(z.array(valueListSummarySchema));

/** Тип строки формы параметров (ФТ-1.5.1). */
export const attributeInputTypeSchema = z.enum(["list", "manual", "calculated"]);

export type AttributeInputType = z.infer<typeof attributeInputTypeSchema>;

/** Уровень заполнения: константа типоразмера или ввод при расчёте (ЧТЗ 10.1). */
export const calcScopeSchema = z.enum(["unit", "calculation"]);

export type CalcScope = z.infer<typeof calcScopeSchema>;

/** Уровень доступа к результату тягового расчёта (ФТ-4.4–4.7). */
export const resultTierSchema = z.enum(["brief", "full"]);

export type ResultTier = z.infer<typeof resultTierSchema>;

/** Сводка формулы для выбора в схеме модели; ведение формул — E06. */
export const formulaSummarySchema = z.object({
  id: z.string().uuid(),
  expression: z.string().min(1),
  methodologyId: z.string().uuid(),
  versionNo: z.number().int().positive(),
  isActive: z.boolean(),
});

export type FormulaSummary = z.infer<typeof formulaSummarySchema>;

/**
 * Атрибут в контексте модели: порядок, видимость, обязательность, формула.
 * Сам атрибут (имя, тип значения) — в справочнике; здесь только поведение в форме.
 */
export const modelAttributeSchema = z.object({
  id: z.string().uuid(),
  modelId: z.string().uuid(),
  attributeId: z.string().uuid(),
  attributeName: nameSchema,
  formulaName: formulaNameSchema.nullable(),
  valueType: attributeValueTypeSchema,
  groupId: z.string().uuid().nullable(),
  groupName: nameSchema.nullable(),
  sortOrder: sortOrderSchema,
  inputType: attributeInputTypeSchema,
  showToClient: z.boolean(),
  showInResult: z.boolean(),
  isRequired: z.boolean(),
  allowManualOverride: z.boolean(),
  formulaId: z.string().uuid().nullable(),
  formulaExpression: z.string().nullable(),
  defaultValue: z.string().max(512).nullable(),
  scope: calcScopeSchema,
  resultTier: resultTierSchema.nullable(),
});

export type ModelAttribute = z.infer<typeof modelAttributeSchema>;

const modelAttributeFieldsSchema = z.object({
  attributeId: z.string().uuid(),
  groupId: z.string().uuid().nullable().default(null),
  sortOrder: sortOrderSchema.default(0),
  inputType: attributeInputTypeSchema,
  showToClient: z.boolean().default(true),
  showInResult: z.boolean().default(false),
  isRequired: z.boolean().default(false),
  allowManualOverride: z.boolean().default(false),
  formulaId: z.string().uuid().nullable().default(null),
  defaultValue: z.string().trim().max(512).nullable().default(null),
  scope: calcScopeSchema.default("calculation"),
  resultTier: resultTierSchema.nullable().default(null),
});

/** Тело `POST /api/v1/admin/models/{modelId}/attributes`. */
export const createModelAttributeRequestSchema = modelAttributeFieldsSchema;

export type CreateModelAttributeRequest = z.infer<typeof createModelAttributeRequestSchema>;

/** Тело `PATCH /api/v1/admin/models/{modelId}/attributes/{id}`. */
export const updateModelAttributeRequestSchema = modelAttributeFieldsSchema
  .omit({ attributeId: true })
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: "Нет полей для изменения" });

export type UpdateModelAttributeRequest = z.infer<typeof updateModelAttributeRequestSchema>;

export const modelAttributeListResponseSchema = apiSuccessSchema(z.array(modelAttributeSchema));
export const modelAttributeResponseSchema = apiSuccessSchema(modelAttributeSchema);
export const formulaSummaryListResponseSchema = apiSuccessSchema(z.array(formulaSummarySchema));
